import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.js';
import { onOrchestratorAsk } from './orchestratorBus.js';
const TBL = { width: '100%', borderCollapse: 'collapse', fontSize: 12, margin: '6px 0' };
const TH = { textAlign: 'left', borderBottom: '1px solid var(--line)', padding: '4px 6px', fontWeight: 600 };
const TD = { borderBottom: '1px solid var(--line)', padding: '4px 6px', verticalAlign: 'top' };
/** A markdown table separator row, e.g. `|---|:--:|`. */
function isTableSep(line) {
    return /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && line.includes('-');
}
/** Split a `| a | b |` row into trimmed cells. */
function splitRow(line) {
    let s = line.trim();
    if (s.startsWith('|'))
        s = s.slice(1);
    if (s.endsWith('|'))
        s = s.slice(0, -1);
    return s.split('|').map((c) => c.trim());
}
/** Inline markdown: just bold (**text**); everything else passes through as text. Safe (no HTML). */
function mdInline(text, keyBase) {
    const out = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0;
    let i = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last)
            out.push(text.slice(last, m.index));
        out.push(_jsx("strong", { children: m[1] ?? '' }, `${keyBase}-b${i++}`));
        last = m.index + m[0].length;
    }
    if (last < text.length)
        out.push(text.slice(last));
    return out;
}
/** A safe, minimal markdown block renderer (headings, bullet lists, paragraphs, bold) for AI answers. */
function renderMarkdown(text) {
    const blocks = [];
    let list = [];
    let para = [];
    let key = 0;
    const flushPara = () => {
        if (para.length) {
            blocks.push(_jsx("p", { style: { margin: '4px 0' }, children: mdInline(para.join(' '), `p${key}`) }, `p${key++}`));
            para = [];
        }
    };
    const flushList = () => {
        if (list.length) {
            const items = list;
            blocks.push(_jsx("ul", { children: items.map((li, j) => _jsx("li", { children: mdInline(li, `u${key}-${j}`) }, j)) }, `u${key++}`));
            list = [];
        }
    };
    const lines = text.split('\n');
    for (let i = 0; i < lines.length; i++) {
        const line = (lines[i] ?? '').trimEnd();
        const next = (lines[i + 1] ?? '').trimEnd();
        // markdown table: a `| ... |` header row followed by a `|---|` separator
        if (line.trim().startsWith('|') && isTableSep(next)) {
            flushPara();
            flushList();
            const header = splitRow(line);
            const rows = [];
            let j = i + 2;
            while (j < lines.length && (lines[j] ?? '').trim().startsWith('|')) {
                rows.push(splitRow(lines[j] ?? ''));
                j++;
            }
            blocks.push(_jsxs("table", { style: TBL, children: [_jsx("thead", { children: _jsx("tr", { children: header.map((c, k) => _jsx("th", { style: TH, children: mdInline(c, `th${key}-${k}`) }, k)) }) }), _jsx("tbody", { children: rows.map((r, ri) => _jsx("tr", { children: header.map((_, ci) => _jsx("td", { style: TD, children: mdInline(r[ci] ?? '', `td${key}-${ri}-${ci}`) }, ci)) }, ri)) })] }, `t${key++}`));
            i = j - 1;
            continue;
        }
        const h = /^(#{1,4})\s+(.*)$/.exec(line);
        const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
        if (h) {
            flushPara();
            flushList();
            blocks.push(_jsx("div", { style: { fontWeight: 600, margin: '8px 0 2px' }, children: mdInline(h[2] ?? '', `h${key}`) }, `h${key++}`));
        }
        else if (bullet) {
            flushPara();
            list.push(bullet[1] ?? '');
        }
        else if (line.trim() === '') {
            flushPara();
            flushList();
        }
        else {
            flushList();
            para.push(line);
        }
    }
    flushPara();
    flushList();
    return _jsx(_Fragment, { children: blocks });
}
const MODULE_LABEL = {
    savings: 'Savings Pipeline', suppliers: 'Suppliers', categories: 'Master Data', spend: 'Spend', contracts: 'Contracts',
};
const GREETING = {
    role: 'ai',
    text: 'I can answer questions about the live data on this screen and explain what I am seeing. I draft and analyse; you decide and act. Ask me anything below.',
};
function sourceLine(sources) {
    const labels = sources.slice(0, 5).map((s) => s.label);
    const more = sources.length > 5 ? ` +${sources.length - 5} more` : '';
    const mod = sources[0]?.module.toUpperCase() ?? 'RECORDS';
    return `GROUNDED ON ${sources.length} ${mod} RECORD(S): ${labels.join(' · ')}${more}`;
}
/** The original approved static panel, shown verbatim when there is no live adapter (preview build). */
function StaticPanel() {
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "orc-body", children: _jsxs("div", { className: "orc-stream", children: [_jsxs("div", { className: "msg-ai", children: ["I read this category from your master data and live intelligence. Ask me to draft the strategy, or pick a prompt below.", _jsx("div", { className: "src", children: "SOURCES: MASTER DATA \u00B7 CATEGORY INTELLIGENCE \u00B7 MEGA TRENDS" })] }), _jsxs("div", { className: "suggest", children: [_jsx("div", { className: "sl", children: "Suggested next" }), _jsx("button", { className: "sug", children: "Draft the strategy statement" }), _jsx("button", { className: "sug", children: "What changed in this market this month?" })] })] }) }), _jsxs("div", { className: "orc-input", children: [_jsx("button", { className: "attach-btn", title: "Attach files. They are saved to your own folder.", children: _jsx(Icon, { name: "i-clip" }) }), _jsx("input", { placeholder: "Ask anything, or drop files here." }), _jsx("button", { className: "btn btn-primary", children: "\u2192" })] }), _jsx("div", { className: "drop-hint", children: "FILES YOU ADD ARE STORED IN YOUR OWN FOLDER: ONEDRIVE / PROCUAI DATA" })] }));
}
export function OrchestratorChat({ adapter }) {
    const [messages, setMessages] = useState([GREETING]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const bodyRef = useRef(null);
    // Holds the latest send() so the bus listener (subscribed once) always calls the current closure.
    const sendRef = useRef(() => undefined);
    useEffect(() => {
        const el = bodyRef.current;
        if (el)
            el.scrollTop = el.scrollHeight;
    }, [messages, busy]);
    // A screen's own prompt chips can ask the live Orchestrator via the bus (e.g. Stakeholder Map).
    useEffect(() => onOrchestratorAsk((q) => sendRef.current(q)), []);
    if (!adapter)
        return _jsx(StaticPanel, {});
    const groundable = adapter.module !== null;
    const canAsk = groundable && !busy;
    const send = (q) => {
        const question = q.trim();
        if (!question || !groundable || busy)
            return;
        setMessages((m) => [...m, { role: 'user', text: question }]);
        setInput('');
        setBusy(true);
        adapter
            .ask(question)
            .then((r) => setMessages((m) => [
            ...m,
            { role: 'ai', text: r.answer, sources: r.sources, followups: r.followups, flagged: r.usage.injectionFlagged, unavailable: r.usage.modelUnavailable, draft: r.draft, q: question },
        ]))
            .catch((e) => setMessages((m) => [...m, { role: 'ai', text: e.message || 'Something went wrong. Please try again.', error: true }]))
            .finally(() => setBusy(false));
    };
    sendRef.current = send;
    const patchAi = (idx, patch) => setMessages((m) => m.map((x, i) => (i === idx && x.role === 'ai' ? { ...x, ...patch } : x)));
    const insertDraft = (idx, draft) => {
        if (!adapter.applyDraft)
            return;
        patchAi(idx, { inserting: true });
        adapter
            .applyDraft(draft.payload)
            .then((res) => patchAi(idx, { inserting: false, insertedId: res.id }))
            .catch((e) => { patchAi(idx, { inserting: false }); setMessages((m) => [...m, { role: 'ai', text: e.message || 'Could not insert. Please try again.', error: true }]); });
    };
    const undoDraft = (idx, id) => {
        if (!adapter.undo)
            return;
        adapter.undo(id).then(() => patchAi(idx, { insertedId: null, discarded: true })).catch((e) => setMessages((m) => [...m, { role: 'ai', text: e.message || 'Could not undo.', error: true }]));
    };
    const rate = (idx, rating, question, answer) => {
        if (!adapter.feedback)
            return;
        patchAi(idx, { rated: rating });
        void adapter.feedback({ module: adapter.module ?? '', question, answer, rating }).catch(() => undefined);
    };
    const placeholder = groundable
        ? 'Ask about this screen.'
        : 'Open a module from the left to chat about it.';
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "orc-body", ref: bodyRef, children: _jsxs("div", { className: "orc-stream", children: [messages.map((m, i) => m.role === 'user' ? (_jsx("div", { className: "msg-user", children: m.text }, i)) : (_jsxs("div", { className: "msg-ai", children: [renderMarkdown(m.text), m.unavailable && _jsx("div", { className: "src", children: "AI MODEL NOT CONFIGURED IN THIS ENVIRONMENT - SHOWING GROUNDED SOURCES ONLY" }), m.flagged && _jsx("div", { className: "src", children: "UNTRUSTED CONTENT IN YOUR DATA WAS NEUTRALISED AND NOT EXECUTED" }), m.sources && m.sources.length > 0 && _jsx("div", { className: "src", children: sourceLine(m.sources) }), m.followups && m.followups.length > 0 && (_jsxs("div", { className: "suggest", children: [_jsx("div", { className: "sl", children: "Suggested next" }), m.followups.map((f, j) => (_jsx("button", { className: "sug", onClick: () => send(f), disabled: !canAsk, children: f }, j)))] })), m.draft && adapter.applyDraft && (m.insertedId ? (_jsxs("div", { className: "insert-confirm show", children: ["\u2713 Inserted into ", MODULE_LABEL[m.draft.module] ?? m.draft.module, adapter.undo ? _jsxs(_Fragment, { children: [" \u00B7 ", _jsx("a", { href: "#", className: "undo", onClick: (e) => { e.preventDefault(); undoDraft(i, m.insertedId); }, children: "Undo" })] }) : null] })) : m.discarded ? (_jsx("div", { className: "src", children: "DRAFT NOT SAVED" })) : (_jsxs("div", { children: [_jsxs("div", { style: { border: '1px solid var(--line)', borderRadius: 10, background: 'var(--paper)', padding: '9px 11px', margin: '8px 0' }, children: [_jsxs("div", { style: { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 6 }, children: [m.draft.summary, " \u00B7 review before saving"] }), m.draft.fields.map((f, k) => (_jsxs("div", { style: { fontSize: 12.5, display: 'flex', gap: 8, padding: '1px 0' }, children: [_jsx("span", { style: { color: 'var(--ink-soft)', minWidth: 92 }, children: f.label }), _jsx("b", { children: f.value })] }, k)))] }), _jsxs("div", { className: "insert-row", children: [_jsx("button", { className: "btn-insert", onClick: () => insertDraft(i, m.draft), disabled: m.inserting, children: m.inserting ? 'Inserting…' : '⤓ Insert into module' }), _jsx("button", { className: "btn-ghost", onClick: () => patchAi(i, { discarded: true }), children: "Discard" })] })] }))), m.q && adapter.feedback && (_jsxs("div", { style: { display: 'flex', gap: 4, marginTop: 8 }, children: [_jsx("button", { title: "Helpful", "aria-label": "Helpful", onClick: () => rate(i, 1, m.q ?? '', m.text), style: { background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: m.rated === 1 ? 'var(--pine)' : 'var(--ink-soft)' }, children: _jsx(Icon, { name: "i-thumb-up", size: "sm" }) }), _jsx("button", { title: "Not helpful", "aria-label": "Not helpful", onClick: () => rate(i, -1, m.q ?? '', m.text), style: { background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: m.rated === -1 ? 'var(--red)' : 'var(--ink-soft)' }, children: _jsx(Icon, { name: "i-thumb-down", size: "sm" }) })] }))] }, i))), busy && (_jsx("div", { className: "msg-ai", children: _jsxs("div", { className: "thinking", children: [_jsx("span", { className: "dot" }), _jsx("span", { className: "dot" }), _jsx("span", { className: "dot" }), " Thinking"] }) })), messages.length === 1 && adapter.prompts && adapter.prompts.length > 0 && (_jsxs("div", { className: "suggest", children: [_jsx("div", { className: "sl", children: "Try" }), adapter.prompts.map((p, j) => (_jsx("button", { className: "sug", onClick: () => send(p), disabled: !canAsk, children: p }, j)))] }))] }) }), _jsxs("div", { className: "orc-input", children: [_jsx("button", { className: "attach-btn", title: "Attach files. They are saved to your own folder.", children: _jsx(Icon, { name: "i-clip" }) }), _jsx("input", { placeholder: placeholder, value: input, onChange: (e) => setInput(e.target.value), onKeyDown: (e) => { if (e.key === 'Enter')
                            send(input); }, disabled: !canAsk }), _jsx("button", { className: "btn btn-primary", onClick: () => send(input), disabled: !canAsk, "aria-label": "Send", children: "\u2192" })] }), _jsx("div", { className: "drop-hint", children: "FILES YOU ADD ARE STORED IN YOUR OWN FOLDER: ONEDRIVE / PROCUAI DATA" })] }));
}
//# sourceMappingURL=OrchestratorChat.js.map